import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { HiOutlineMenuAlt4, HiOutlineX } from 'react-icons/hi';
import { NAV_LINKS, CONFERENCE } from '../../data/conference';
import { ButtonNav } from '../ui';

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: background 0.3s var(--ease-out), border-color 0.3s var(--ease-out),
    box-shadow 0.3s var(--ease-out);
  border-bottom: 1px solid transparent;

  ${(p) =>
    p.$scrolled &&
    css`
      background: rgba(236, 233, 225, 0.82);
      backdrop-filter: blur(14px);
      border-bottom-color: var(--line);
      box-shadow: 0 12px 32px -18px rgba(0, 0, 0, 0.8);
    `}
`;

const Inner = styled.div`
  max-width: var(--container);
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 4vw, 2.5rem);
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex: none;

  /* colored IEMENTech mark — reads directly on the light bar */
  img {
    width: 46px;
    height: 46px;
    object-fit: contain;
  }
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;

  .name {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.05rem;
    letter-spacing: 0.01em;
    color: var(--ink);

    span {
      color: var(--copper);
    }
  }

  .sub {
    font-family: var(--font-mono);
    font-size: 0.58rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-top: 0.2rem;
  }
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const Item = styled(NavLink)`
  font-family: var(--font-mono);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--ink-dim);
  padding: 0.6rem 0.85rem;
  transition: color 0.2s;
  position: relative;

  &:hover {
    color: var(--ink);
  }

  &.active {
    color: var(--copper-bright);
  }

  &.active::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0.15rem;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--copper);
  }
`;

const RegisterCta = styled(ButtonNav)`
  flex: none;

  &:hover {
    transform: translateY(-1px);
  }

  @media (max-width: 1080px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;
  background: transparent;
  color: var(--ink);
  font-size: 1.7rem;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  border: 1px solid var(--line);

  @media (max-width: 1080px) {
    display: inline-flex;
  }
`;

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(236, 233, 225, 0.97);
  backdrop-filter: blur(18px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6rem clamp(1.5rem, 8vw, 3.5rem) 3rem;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition: opacity 0.32s var(--ease-out), visibility 0.32s;
`;

const SheetItem = styled(NavLink)`
  font-family: var(--font-display);
  font-size: clamp(1.7rem, 6.5vw, 2.6rem);
  font-weight: 700;
  color: var(--ink-dim);
  padding: 0.45rem 0;
  display: flex;
  align-items: baseline;
  gap: 1rem;
  transition: color 0.2s, transform 0.32s var(--ease-out), opacity 0.32s;
  transform: ${(p) => (p.$open ? 'translateY(0)' : 'translateY(20px)')};
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transition-delay: ${(p) => p.$index * 45}ms;

  .idx {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: 0.15em;
    color: var(--copper);
  }

  &:hover,
  &.active {
    color: var(--ink);
  }
`;

const SheetFootnote = styled.p`
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 3rem;
`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the sheet and reset scroll on navigation
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <Bar $scrolled={scrolled || open}>
        <Inner>
          <Brand to="/" aria-label="IEMENTech 2027 home">
            <img src="/assets/logo2.png" alt="" />
            <BrandText>
              <span className="name">
                IEMENTech <span>'27</span>
              </span>
              <span className="sub">10th Intl. Conference</span>
            </BrandText>
          </Brand>

          <Links aria-label="Primary navigation">
            {NAV_LINKS.filter((l) => l.to !== '/registration').map((link) => (
              <Item key={link.to} to={link.to} end={link.to === '/'}>
                {link.label}
              </Item>
            ))}
          </Links>

          <RegisterCta forwardedAs={Link} to="/registration">
            Register
          </RegisterCta>

          <Burger
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <HiOutlineX /> : <HiOutlineMenuAlt4 />}
          </Burger>
        </Inner>
      </Bar>

      <Sheet $open={open} aria-hidden={!open}>
        {NAV_LINKS.map((link, i) => (
          <SheetItem
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            $open={open}
            $index={i}
            tabIndex={open ? 0 : -1}
          >
            <span className="idx">/{String(i + 1).padStart(2, '0')}</span>
            {link.label}
          </SheetItem>
        ))}
        <SheetFootnote>
          {CONFERENCE.dateLabel} · {CONFERENCE.venueShort}
        </SheetFootnote>
      </Sheet>
    </>
  );
}
